import { FC, memo, useEffect, useRef, useState, useCallback } from "react";
import { useBoostTimer, BOOST_COOLDOWN } from "../../hooks/useBoostTimer";
import styles from "./BoostButton.module.css";
import boostIcon from "/Rewind.svg";
import { useModalStore } from "../../../../Store/ModalStore";
import { useMiningStore } from "../../../../Store/miningStore";
import { BoostMiningService } from "../../services/BoostMiningService";
import { boostMining } from "../../../../Services/Requests";
import { useNotificationStore } from "../../../../Store/NotificationStore";
import { MiningService } from "../../services/MiningService";
import { useBoostTaskStore } from "../../../../Store/BoostTaskStore";
import { useCPMallowedStore } from "../../../../Store/CPMallowedStore";
import { useIfModalOpenedStore } from "../../../../Store/IfModalOpened";

interface BoostButtonProps {
  className?: string;
  onClick: () => Promise<void>;
}

const CANVAS_SIZE = 48; // Размер канваса

const BoostButton: FC<BoostButtonProps> = memo(({ className = "" }) => {
  const { isBoostAvailable, remainingTime } = useBoostTimer();
  const [isBoostInProgress, setIsBoostInProgress] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const boostInProgressRef = useRef(false);
  const miningData = useMiningStore((state) => state.miningData);
  const { setNotification } = useNotificationStore();
  const [, forceUpdate] = useState({});
  const cpmAllowed = useCPMallowedStore((state) => state.cpmAllowed);
  const setCPMallowed = useCPMallowedStore((state) => state.setCPMallowed);
  const ifModalOpened = useIfModalOpenedStore((state) => state.ifModalOpened);

  const drawProgress = useCallback(
    (progress: number) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

      if (isBoostAvailable) {
        if (isBoostInProgress) {
          // Анимация загрузки
          const time = Date.now() / 1000;
          const angle = (time % 1) * Math.PI * 2;

          // Фоновый серый круг
          ctx.beginPath();
          ctx.arc(
            CANVAS_SIZE / 2,
            CANVAS_SIZE / 2,
            (CANVAS_SIZE - 2) / 2,
            0,
            2 * Math.PI
          );
          ctx.strokeStyle = "rgba(187, 185, 185, 0.2)";
          ctx.lineWidth = 2;
          ctx.stroke();

          // Анимированный зеленый круг
          ctx.beginPath();
          ctx.arc(
            CANVAS_SIZE / 2,
            CANVAS_SIZE / 2,
            (CANVAS_SIZE - 2) / 2,
            angle - Math.PI / 2,
            angle + Math.PI / 2
          );
          ctx.strokeStyle = "#4CAF50";
          ctx.lineWidth = 2;
          ctx.stroke();
        } else {
          // Статичный зеленый круг
          ctx.beginPath();
          ctx.arc(
            CANVAS_SIZE / 2,
            CANVAS_SIZE / 2,
            (CANVAS_SIZE - 2) / 2,
            0,
            2 * Math.PI
          );
          ctx.strokeStyle = "#4CAF50";
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      } else {
        // Если идет кулдаун - рисуем серый фон и зеленый прогресс
        ctx.beginPath();
        ctx.arc(
          CANVAS_SIZE / 2,
          CANVAS_SIZE / 2,
          (CANVAS_SIZE - 2) / 2,
          0,
          2 * Math.PI
        );
        ctx.strokeStyle = "rgba(187, 185, 185, 0.2)";
        ctx.lineWidth = 2;
        ctx.stroke();

        if (progress > 0 && progress < 1) {
          ctx.beginPath();
          ctx.arc(
            CANVAS_SIZE / 2,
            CANVAS_SIZE / 2,
            (CANVAS_SIZE - 2) / 2,
            -Math.PI / 2,
            2 * Math.PI * progress - Math.PI / 2
          );
          ctx.strokeStyle = "#4CAF50";
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      }
    },
    [isBoostAvailable, isBoostInProgress]
  );

  const startTimer = useCallback(() => {
    if (!remainingTime || remainingTime === "0:00") return;

    const [minutes, seconds] = remainingTime.split(":").map(Number);
    const totalRemainingSeconds = minutes * 60 + seconds;
    const progress = 1 - totalRemainingSeconds / BOOST_COOLDOWN;

    const animate = () => {
      drawProgress(progress);
      if (remainingTime !== "0:00" && !isBoostInProgress) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        drawProgress(0);
      }
    };

    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    animate();
  }, [remainingTime, drawProgress, isBoostInProgress]);

  // Эффект для инициализации canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = CANVAS_SIZE * dpr;
      canvas.height = CANVAS_SIZE * dpr;

      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.scale(dpr, dpr);
        ctx.lineCap = "round";
      }
    }
  }, []);

  // Эффект для обновления анимации при изменении remainingTime
  useEffect(() => {
    if (remainingTime !== "0:00") {
      startTimer();
    } else {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      drawProgress(0);
    }
  }, [remainingTime, startTimer, drawProgress]);

  // Эффект для анимации загрузки
  useEffect(() => {
    if (isBoostInProgress) {
      const animateLoading = () => {
        drawProgress(0);
        if (isBoostInProgress) {
          animationRef.current = requestAnimationFrame(animateLoading);
        }
      };
      animateLoading();
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isBoostInProgress, drawProgress]);

  const handleClick = async () => {
    if (
      !isBoostAvailable ||
      isBoostInProgress ||
      boostInProgressRef.current ||
      !miningData?.mining.id
    ) {
      console.log(
        "BoostButton: Click ignored (not available, in progress, or no mining ID)"
      );
      return;
    }

    console.log("BoostButton: Clicked");
    setIsBoostInProgress(true);
    boostInProgressRef.current = true;

    try {
      let currentMiningId = miningData.mining.id;

      if (miningData.mining.status === "claim") {
        try {
          setNotification({
            type: "Error",
            message: "Майнинг уже завершен",
          });
          return;
        } catch (error) {
          console.error("BoostButton: Error in claim sequence:", error);
          setNotification({
            type: "Error",
            message: "Не удалось собрать награду, попробуй еще раз",
          });
          setIsBoostInProgress(false);
          boostInProgressRef.current = false;
          return;
        }
      }

      console.log("BoostButton: Getting all available tasks...");

      const availableTasks = cpmAllowed
        ? await BoostMiningService.getAllAvailableTasks(currentMiningId)
        : await BoostMiningService.getAllAvailableTasksNoCpm(currentMiningId);

      let boostApplied = false;

      if (availableTasks.length > 0) {
        console.log(
          `BoostButton: Found ${availableTasks.length} available tasks. Iterating...`
        );

        for (const task of availableTasks) {
          console.log(
            `BoostButton: Trying task from provider: ${task.provider}`
          );

          const showGenericModal =
            task.provider === "Barza" ||
            task.provider === "TappAds" ||
            task.provider === "Flyer" ||
            task.provider === "DreamCoin" ||
            task.provider === "Barzha" ||
            task.provider === "Adexium";

          if (showGenericModal) {
            console.log(
              `BoostButton: Provider ${task.provider} requires generic modal. Opening...`
            );
            useBoostTaskStore.getState().setCurrentTask(task);
            useModalStore.getState().setOpen("boost");
            boostApplied = true;
            break;
          } else {
            console.log(
              `BoostButton: Provider ${task.provider} uses direct execution. Calling onClick...`
            );
            const result = await task.onClick();
            console.log(
              `BoostButton: Result from ${task.provider}.onClick: ${result}`
            );

            if (result === "success") {
              console.log(
                `BoostButton: Task from ${task.provider} succeeded. Applying boost...`
              );
              boostApplied = true;

              try {
                const response = await boostMining(
                  currentMiningId,
                  task.provider.toLowerCase(),
                  setNotification
                );
                if (response?.last_boost_time) {
                  console.log(
                    "BoostButton: Boost applied successfully via API"
                  );
                  useMiningStore
                    .getState()
                    .setLastBoostTime(response.last_boost_time);
                  setNotification({
                    type: "Success",
                    message: "Майнинг ускорен на 30 минут! 🚀",
                  });
                } else {
                  console.warn(
                    "BoostButton: Boost endpoint did not return last_boost_time"
                  );
                  setNotification({
                    type: "Error",
                    message: "Не удалось применить ускорение. Попробуй позже.",
                  });
                }
              } catch (error) {
                console.error(
                  "BoostButton: Error applying boost via API:",
                  error
                );
                setNotification({
                  type: "Error",
                  message: "Не удалось ускорить майнинг, попробуй позже",
                });
              }
              break;
            } else if (result === "user_cancelled") {
              console.log(
                `BoostButton: Task from ${task.provider} was cancelled by user. Stopping boost attempts.`
              );
              boostApplied = false;
              break;
            } else {
              console.log(
                `BoostButton: Task from ${task.provider} failed. Trying next provider...`
              );
              boostApplied = false;
            }
          }
        }
      } else {
        console.log("BoostButton: No available tasks found from any provider.");
      }

      if (!boostApplied) {
        console.log(
          "BoostButton: Boost process finished without success or modal opening."
        );
        BoostMiningService.resetBoostState();
      }
    } catch (error) {
      console.error("BoostButton: General error during handleClick:", error);
      setNotification({
        type: "Error",
        message: "Что-то пошло не так при поиске ускорения, попробуй еще раз",
      });
      BoostMiningService.resetBoostState();
    } finally {
      setIsBoostInProgress(false);

      boostInProgressRef.current = false;

      if (!ifModalOpened && !cpmAllowed) {
        setNotification({
          type: "Error",
          message: "Нет доступной рекламы. Попробуйте позже.",
        });
      }
      console.log("BoostButton: handleClick finished.");

      try {
        const data = await MiningService.fetchMiningData();
        // useMiningStore.getState().setMiningData(data);
        setCPMallowed(data.cpm_banners_allowed);
      } catch (error) {
        console.error("BoostButton: Error updating mining data:", error);
      }
    }
  };

  return (
    <div>
      <button
        className={`${styles.boostButton} ${className} ${
          !isBoostAvailable ? styles.disabled : ""
        }`}
        onClick={handleClick}
        disabled={
          !isBoostAvailable || isBoostInProgress || boostInProgressRef.current
        }
      >
        <div className={styles.boostButtonContent}>
          <canvas
            ref={canvasRef}
            className={styles.progressCanvas}
            width={CANVAS_SIZE}
            height={CANVAS_SIZE}
          />
          {!isBoostAvailable ? (
            <span className={styles.timer}>{remainingTime}</span>
          ) : (
            <img src={boostIcon} alt="Boost" />
          )}
        </div>
      </button>
    </div>
  );
});

BoostButton.displayName = "BoostButton";

export default BoostButton;
